/**
 * Responsiveness constants shared by the dashboard grid and the app chrome.
 *
 * Two distinct boundary systems (intentionally — see CLAUDE.md):
 *  - **Viewport** boundaries (`MOBILE_*`) drive chrome/modals via `useMediaQuery`
 *    and align to Tailwind's default `lg` (1024px).
 *  - **Container-width** boundaries (`GRID_*`) drive react-grid-layout v2's
 *    responsive columns and are measured off the grid container, not the viewport.
 */

/** Viewport width (Tailwind `lg`) at/above which we treat the app as desktop. */
export const MOBILE_BREAKPOINT_PX = 1024;

/** Matches phones + tablets (below desktop). Mirrors the old ad-hoc query. */
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`;

/**
 * react-grid-layout v2 responsive config, keyed by **measured container width**.
 * We persist only the `lg` (12-col) layout to `config.grid` — see the
 * `react-grid-layout-v2` / `widget-size-pipeline` notes — and let RGL auto-derive
 * the narrower breakpoints from it (no extra storage).
 *
 *  - `lg`  (>=996px container): the full 12-col desktop layout (the stored one).
 *  - `md`  (640-996px):         re-packed into 6 cols (tablets).
 *  - `xs`  (<640px):            4 cols (phones). RGL clamps each item's width to
 *    the column count, so wide widgets still span full-width while small tiles
 *    (a 1x1 App shortcut, etc.) keep a compact, desktop-like footprint instead
 *    of stretching into full-width banners. 4 cols keeps the mobile column width
 *    (~88px on a 360px phone) close to the desktop column width (1200/12=100px),
 *    so widgets render at roughly their real size and simply wrap.
 *
 * Tune these thresholds with visual QA; `rowHeight` stays at the historical 90.
 */
export const GRID_BREAKPOINTS: Record<string, number> = { lg: 996, md: 640, xs: 0 };
export const GRID_COLS: Record<string, number> = { lg: 12, md: 6, xs: 4 };
export const GRID_ROW_HEIGHT = 90;

/** Only layout changes made at this breakpoint are persisted to `config.grid`. */
export const GRID_PERSIST_BREAKPOINT = "lg";

/** Widest grid canvas (historical desktop width); the container caps here. */
export const GRID_MAX_WIDTH_PX = 1200;
